import { createContext, useRef, useCallback, useEffect, useContext } from 'react'

import { useParams } from 'react-router-dom'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { socket } from '../../../tool/socketIO'

import { createTelemetryOutbox, saveTelemetry } from '../service/telemetryService'
import { isStudySession, studyTaskMetadata } from '../study/studyConfig'
import { codeHash } from '../../session/testResults'

import { SESSION_EVENTS, CHAT_EVENTS, SYSTEM_EVENTS, STUDY_EVENTS } from '../constants/telemetryEvents'

export const TelemetryContext = createContext()

const FLUSH_INTERVAL_MS = 5000
const HEARTBEAT_INTERVAL_MS = 15000

// Study conditions number every event, so a reconstruction can put them in order and see
// which never arrived: client_session_id is random per page load (a reload starts a new
// run), seq counts 1, 2, 3, ... within it, and event_id is the two together. A batch sent
// twice (resent after a timeout the server in fact survived) keeps its event_ids, so the
// copy can be removed.
//
// 16 random base-36 characters (about 80 bits): unique across every page load of the study,
// and short, since it is repeated on every event, mouse moves included.
const newClientSessionId = () => {
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes, (byte) => (byte % 36).toString(36)).join('')
}
const CLIENT_SESSION_ID = newClientSessionId()
let lastSeq = 0

const numberEvent = (event) => {
  lastSeq += 1
  return { ...event, client_session_id: CLIENT_SESSION_ID, seq: lastSeq, event_id: `${CLIENT_SESSION_ID}:${lastSeq}` }
}

// Not something the student did, so they do not end a CHAT_RESPONSE's reading time.
const PASSIVE_EVENTS = new Set([
  ...Object.values(SYSTEM_EVENTS),
  STUDY_EVENTS.PROBE_SHOWN,
  STUDY_EVENTS.PROBES_COMPLETED,
  STUDY_EVENTS.AI_UNLOCKED,
  STUDY_EVENTS.AI_AVAILABILITY,
])

export const TelemetryProvider = ({ children }) => {
  const { id: sessionId } = useParams()
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)
  const userId = currentUser?.id

  // Recording gate: a session that has never opted into the instructor-controlled timer
  // (duration_minutes null - see SessionLobby.jsx) keeps recording always, matching existing
  // behavior for the vast majority of live sessions. A session that HAS opted in only
  // records while status is 'active' - not before the instructor presses Start, not after
  // Stop/the timer running out. Kept in a ref (not a logTelemetryEvent dependency) so the
  // provider stays mounted and the callback identity stays stable across every session poll
  // tick - only the gate value updates, dropping events rather than buffering them while
  // paused so a long pre-start/post-end wait can't build an unbounded backlog to flush later.
  // Study conditions skip the gate (see isStudyRef below).
  const shouldRecordRef = useRef(true)
  useEffect(() => {
    shouldRecordRef.current = session?.duration_minutes == null || session?.status === 'active'
  }, [session?.duration_minutes, session?.status])

  // Study conditions (studyConfig.js) record more, and more reliably: numbered events, a
  // heartbeat, network and socket changes, the editor's cursor and keys, and everything for
  // as long as the page is open, whatever the session's status - the concept probes come
  // after the coding time is up. Worked out during render, not in an effect: the children's
  // mount effects run before this provider's, and their first events must be numbered too.
  // Once a study condition, always one for this page, so a session update without its
  // test_code cannot switch half the events back to the regular shape.
  const isStudyRef = useRef(false)
  if (isStudySession(session)) isStudyRef.current = true
  const isStudy = isStudyRef.current
  // The study task this page shows (studyTaskMetadata), kept as its first one, for SESSION_START.
  const taskRef = useRef(null)
  if (!taskRef.current) taskRef.current = studyTaskMetadata(session)

  // 1. Temporary Buffer (Flushed to DB every 5s)
  const logsBuffer = useRef([])

  // 2. Permanent Store (Keeps everything for immediate local replay)
  const allEvents = useRef([])

  // Tracks the timestamp of the most recent unread CHAT_RESPONSE, so the
  // student's next action (of any kind) can be timed as "reading time."
  const lastChatResponseTimeRef = useRef(null)

  const logTelemetryEvent = useCallback((type, payload) => {
    const numbered = isStudyRef.current
    if (!numbered && !shouldRecordRef.current) return

    const envelope = (event) => (numbered ? numberEvent(event) : event)

    const event = envelope({
      type,
      payload,
      timestamp: Date.now(),
      sessionId: sessionId ? +sessionId : null
    })

    // Push to Buffer (for DB)
    logsBuffer.current.push(event)

    // Push to Permanent Store (for Replay)
    allEvents.current.push(event)

    if (type === CHAT_EVENTS.RESPONSE) {
      lastChatResponseTimeRef.current = event.timestamp
    } else if (lastChatResponseTimeRef.current !== null && !PASSIVE_EVENTS.has(type)) {
      const readEvent = envelope({
        type: CHAT_EVENTS.RESPONSE_READ,
        payload: { reading_time_ms: event.timestamp - lastChatResponseTimeRef.current },
        timestamp: event.timestamp,
        sessionId: sessionId ? +sessionId : null
      })
      lastChatResponseTimeRef.current = null
      logsBuffer.current.push(readEvent)
      allEvents.current.push(readEvent)
    }
  }, [sessionId])

  // The code the editor opened with, from CodeEditor's mount effect - which runs before the
  // SESSION_START effect below, CodeEditor being a descendant of this provider.
  const initialCodeRef = useRef(null)
  const noteInitialCode = useCallback((code) => {
    if (initialCodeRef.current === null && typeof code === 'string') initialCodeRef.current = code
  }, [])

  // Log session start on mount
  useEffect(() => {
    const code = initialCodeRef.current
    logTelemetryEvent(SESSION_EVENTS.START, {
      window_width: window.innerWidth,
      window_height: window.innerHeight,
      timestamp: Date.now(),
      // Study conditions: the code on screen at the start - after a reload the student's
      // saved work, not the starter - for replaying the edits that follow. `code` is the
      // field the replay tools already take a snapshot from; code_hash is the same hash a
      // study run's TERMINAL_RUN carries.
      ...(isStudyRef.current && code !== null ? { code, code_hash: codeHash(code) } : {}),
      // Study conditions: which exercise, and which versions of its task text, provided code
      // and tests (studyTaskMetadata).
      ...(isStudyRef.current && taskRef.current ? { task: taskRef.current } : {})
    })
  }, [logTelemetryEvent])

  // Helper to retrieve full history for the Replay Modal
  const getAllEvents = useCallback(() => {
    return allEvents.current
  }, [])

  // Delivery. Regular sessions send exactly as they always have: each tick's batch posted
  // once (saveTelemetry), a failed one dropped. Study conditions go through telemetryService's
  // outbox: one request at a time, failed batches kept and resent with backoff, and a
  // keepalive flush when the page may be going away. Set up once per mount, reading the
  // latest ids from refs, so batches waiting on a retry survive the sign-in and session
  // updates that used to restart this timer.
  const outboxRef = useRef(null)
  const idsRef = useRef({ sessionId, userId })
  idsRef.current = { sessionId, userId }
  const logRef = useRef(logTelemetryEvent)
  logRef.current = logTelemetryEvent

  useEffect(() => {
    // Only the outbox knows when a batch failed, and it only carries study conditions' batches.
    const report = (type) => (detail) => {
      if (isStudyRef.current) logRef.current(type, detail)
    }
    const outbox = createTelemetryOutbox({
      onSyncFailed: report(SYSTEM_EVENTS.SYNC_FAILED),
      onSyncRecovered: report(SYSTEM_EVENTS.SYNC_RECOVERED),
    })
    outboxRef.current = outbox

    // Flush logs: hands the buffer over to be sent. As before, events with no session or
    // user to file them under are dropped. sendNow false only queues a study condition's
    // batch, for flushNow's keepalive flush.
    const flush = ({ sendNow = true } = {}) => {
      if (logsBuffer.current.length === 0) return

      // Swap the buffer
      const batch = logsBuffer.current
      logsBuffer.current = []

      const ids = idsRef.current
      if (!ids.sessionId || !ids.userId) return
      if (isStudyRef.current) {
        outbox.enqueue({
          sessionId: ids.sessionId,
          userId: ids.userId,
          events: batch,
          hasEventIds: batch.every((event) => event.event_id),
        }, { sendNow })
      } else {
        saveTelemetry(ids.sessionId, ids.userId, batch)
          .catch(err => console.warn("Telemetry flush failed", err))
      }
    }

    // Every 5 s from mount, as before - except that a study condition's first flush comes at
    // a random point in those 5 s: every page of a class mounts on the same Start push, and
    // would otherwise flush in the same second every 5 s. (WorkspacePage renders this
    // provider only once it has the session, so isStudyRef is already set here.)
    let interval = null
    let firstFlush = null
    if (isStudyRef.current) {
      firstFlush = setTimeout(() => {
        flush()
        interval = setInterval(flush, FLUSH_INTERVAL_MS)
      }, Math.floor(Math.random() * FLUSH_INTERVAL_MS))
    } else {
      interval = setInterval(flush, FLUSH_INTERVAL_MS)
    }

    // A study condition's page may be going away: pagehide covers closing and reloading, and
    // a hidden tab is the last moment a page is sure to run on a phone. The buffer is queued
    // without a normal send starting - one the browser would cancel at unload, and a second
    // copy of the batch if the page lives on - and flushKeepalive sends everything waiting
    // with keepalive, a copy of a batch already in flight too. A send there that gets no
    // answer is simply resent. Other sessions wait for the tick, as before.
    const flushNow = () => {
      if (!isStudyRef.current) return
      flush({ sendNow: false })
      outbox.flushKeepalive()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushNow()
    }
    const onOnline = () => outbox.retryNow()
    window.addEventListener('pagehide', flushNow)
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('online', onOnline)

    return () => {
      clearTimeout(firstFlush)
      clearInterval(interval)
      window.removeEventListener('pagehide', flushNow)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('online', onOnline)
      // A study condition unmounted with the page still open (back to the waiting room,
      // say): the last seconds go out now instead of being lost.
      if (isStudyRef.current) flush()
      outbox.dispose()
      outboxRef.current = null
    }
  }, [])

  // Set by Workspace when a study condition's page is finished (useStudyPhase's 'done': the
  // probes answered, or none to answer). The heartbeat pauses then, so a page left open on its
  // Thank You screen does not post every 15 s for as long as it stays open.
  const studyDoneRef = useRef(false)
  const setStudyDone = useCallback((done) => {
    studyDoneRef.current = Boolean(done)
  }, [])

  // Study conditions: a heartbeat while the page is open, and the network and socket
  // changes in between, so a gap in the events can be told apart from a student doing
  // nothing (the heartbeats go on) and from a closed page (they stop as well).
  useEffect(() => {
    if (!isStudy) return undefined

    const heartbeat = setInterval(() => {
      if (studyDoneRef.current) return
      logTelemetryEvent(SYSTEM_EVENTS.HEARTBEAT, {
        visible: document.visibilityState === 'visible',
        online: navigator.onLine,
        queuedEvents: logsBuffer.current.length + (outboxRef.current?.queuedEvents() || 0),
        lastSeq,
      })
    }, HEARTBEAT_INTERVAL_MS)

    const onOffline = () => logTelemetryEvent(SYSTEM_EVENTS.NETWORK_OFFLINE, {})
    const onOnline = () => logTelemetryEvent(SYSTEM_EVENTS.NETWORK_ONLINE, {})
    // Listening only: socketIO.js's own reconnect handling (joining its rooms again) is
    // untouched.
    const onSocketDisconnect = (reason) => logTelemetryEvent(SYSTEM_EVENTS.SOCKET_DISCONNECT, { reason })
    const onSocketReconnect = (attempt) => logTelemetryEvent(SYSTEM_EVENTS.SOCKET_RECONNECT, { attempt })
    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)
    socket.on('disconnect', onSocketDisconnect)
    socket.io.on('reconnect', onSocketReconnect)

    return () => {
      clearInterval(heartbeat)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
      socket.off('disconnect', onSocketDisconnect)
      socket.io.off('reconnect', onSocketReconnect)
    }
  }, [isStudy, logTelemetryEvent])

  return (
    <TelemetryContext.Provider value={{ logTelemetryEvent, getAllEvents, isStudy, noteInitialCode, setStudyDone }}>
      {children}
    </TelemetryContext.Provider>
  )
}
